import { useTranslation } from 'react-i18next';
import { Alert, Button, Input, Modal, Space, Typography } from 'antd';
import { Copy } from 'lucide-react';
import { toast } from 'sonner';
import type { GeneratedApiKey } from '../types';

const { Text } = Typography;

interface GeneratedKeyModalProps {
  generated: GeneratedApiKey | null;
  onClose: () => void;
}

/**
 * REQ-40 — üretilen/döndürülen anahtarın düz metnini BİR KEZ gösterir. Anahtar
 * yalnızca prop olarak gelir (state'te kalıcı tutulmaz); modal kapanınca çağıran
 * `generated`'ı null'a çeker ve anahtar unutulur. Kopyala butonu panoya alır.
 */
export function GeneratedKeyModal({ generated, onClose }: GeneratedKeyModalProps) {
  const { t } = useTranslation(['api-keys', 'common']);

  const copy = () => {
    if (!generated) return;
    void navigator.clipboard
      .writeText(generated.key)
      .then(() => toast.success(t('generated.copied')))
      .catch(() => toast.error(t('generated.copyFailed')));
  };

  return (
    <Modal
      open={generated !== null}
      title={t('generated.title')}
      onCancel={onClose}
      maskClosable={false}
      destroyOnHidden
      footer={
        <Button type="primary" onClick={onClose}>
          {t('generated.done')}
        </Button>
      }
    >
      {generated ? (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          <Alert type="warning" showIcon message={t('generated.warning')} />
          <div>
            <Text type="secondary">{t('generated.keyLabel')}</Text>
            <Space.Compact style={{ width: '100%', marginTop: 4 }}>
              <Input
                readOnly
                value={generated.key}
                style={{ fontFamily: 'var(--ds-font-mono, ui-monospace, monospace)' }}
                data-testid="generated-api-key"
              />
              <Button icon={<Copy size={14} />} onClick={copy}>
                {t('generated.copy')}
              </Button>
            </Space.Compact>
          </div>
        </Space>
      ) : null}
    </Modal>
  );
}
